// Placeholder imagery across the site: every stock photo is replaced by the MVA logo.
const LOGO = "/logo/logo.png";
const u = (_id: string, _w = 900) => LOGO;

export const photos = {
  heroMain: u("1522202176988-66273c2fd55f", 1000),
  heroSecondary: u("1523240795612-9a054b0db644", 700),
  heroTertiary: u("1531482615713-2afd69097998", 700),
  about: u("1524178232363-1fb2b075b655", 1100),
  cta: u("1517048676732-d65bc937f952", 1400),
  gallery: [
    u("1522202176988-66273c2fd55f", 800),
    u("1521737604893-d14cc237f11d", 800),
    u("1543269865-cbf427effbad", 800),
    u("1531482615713-2afd69097998", 800),
    u("1524178232363-1fb2b075b655", 800),
    u("1523240795612-9a054b0db644", 800),
  ],
};

// About page "Legacy" image, one file per screen size (put the files in public/images/about/).
export const aboutImages = {
  desktop: "/images/about/about-desktop.webp", // 550x450, laptop/desktop (>=1024px)
  tablet: "/images/about/about-tablet.webp", //   768x450, tablet (640-1023px)
  mobile: "/images/about/about-mobile.webp", //   400x300, mobile (<640px)
};

// Home "Pathways to Progress" cards, one file per screen size (put the files in public/images/general/).
const pathway = (name: string) => ({
  desktop: `/images/general/${name}-desktop.webp`, // 600x350, laptop/desktop (>=1024px)
  tablet: `/images/general/${name}-tablet.webp`, //   768x350, tablet (640-1023px)
  mobile: `/images/general/${name}-mobile.webp`, //   400x300, mobile (<640px)
});
export const pathwayImages = {
  career: pathway("pathway-career"),
  team: pathway("pathway-team"),
};

const categoryPhotos: Record<string, string> = {
  Technology: u("1461749280684-dccba630e2f6"),
  Design: u("1558655146-9f40138edfeb"),
  Marketing: u("1460925895917-afdab827c52f"),
  Business: u("1454165804606-c3d57bc86b40"),
  Leadership: u("1552664730-d307ca884978"),
  "Personal Growth": u("1506126613408-eca07ce68773"),
  "Travel & Tourism": u("1488646953014-85cb44e25828"),
  "IT Courses": u("1518770660439-4636190af475"),
  "Personality Development": u("1557804506-669a67965ba0"),
  IELTS: u("1503676260728-1c00da094a0b"),
};

export function courseImage(category: string) {
  return categoryPhotos[category] ?? u("1498050108023-c5249f4df085");
}

const portraits: Record<string, string> = {
  "aarav-patel": u("1507003211169-0a1dd7228f2d", 600),
  "priya-nair": u("1438761681033-6461ffad8d80", 600),
  "rohit-mehta": u("1500648767791-00dcc994a43e", 600),
  "sana-qureshi": u("1494790108377-be9c29b29330", 600),
  "vikram-reddy": u("1472099645785-5658abf4ff4e", 600),
  "meera-shah": u("1580489944761-15a19d654956", 600),
  // testimonials / success stories (keyed by name)
  "Aditi Sharma": u("1573496359142-b8d87734a5a2", 400),
  "Rahul Verma": u("1519085360753-af0119f7cbe7", 400),
  "Priya Menon": u("1544005313-94ddf0286df2", 400),
  "Kavya Iyer": u("1438761681033-6461ffad8d80", 600),
  "Arjun Desai": u("1560250097-0b93528c311a", 600),
  "Fatima Sheikh": u("1494790108377-be9c29b29330", 600),
  "Nikhil Rao": u("1472099645785-5658abf4ff4e", 600),
  // leadership team (about page) - placeholders until real photos arrive
  "Jayanirao": u("1560250097-0b93528c311a", 400),
  "Balaji Suryamaran": u("1507003211169-0a1dd7228f2d", 400),
  "Sateesh Mahamuni": u("1500648767791-00dcc994a43e", 400),
  "Shabbir Dada Bhanu Hameed": u("1472099645785-5658abf4ff4e", 400),
  "Saritha Chirumamidy": u("1494790108377-be9c29b29330", 400),
  "Dr. Imrif Sanejer": u("1519085360753-af0119f7cbe7", 400),
  "Sara Louis": u("1580489944761-15a19d654956", 400),
};

export function portrait(key: string): string | undefined {
  return portraits[key];
}

// Per-course photo overrides so courses in the same category don't share one image.
const courseOverrides: Record<string, string> = {
  "data-science-machine-learning": u("1551288049-bebda4e38f71"),
};

/**
 * True when the course picture should be shown as-is, without the brand colour behind it: an image uploaded in the
 * dashboard, or the placeholder logo (every stock photo is the logo right now, so it would sit on a coloured gradient).
 */
export const hasUploadedCourseImage = (course: { slug: string; category: string; image?: string | null }) =>
  !!course.image?.includes("/images/courses/") || courseImageFor(course) === LOGO;

export function courseImageFor(course: { slug: string; category: string; image?: string | null }) {
  // A picture uploaded in the dashboard (full URL or /path) wins over the stock photo.
  // Served same-origin through the /images/courses rewrite, so it works from any device on the network.
  const upload = course.image?.match(/\/images\/courses\/[^?#]+/)?.[0];
  if (upload) return upload;
  return courseOverrides[course.slug] ?? courseImage(course.category);
}

// Blog post cover placeholders - replace with real article artwork later.
const blogCovers: Record<string, string> = {
  "portfolio-over-resume": u("1521737604893", 1200),
  "learning-how-to-learn": u("1522202176988", 1200),
  "ai-in-the-workplace": u("1518770660439", 1200),
  "why-upskilling-your-workforce-is-critical": u("1552664730-d307ca884978", 1200),
  "why-a-career-in-travel-can-be-more-dynamic": u("1488646953014-85cb44e25828", 1200),
};

export function blogCover(slug: string): string | undefined {
  return blogCovers[slug];
}
